import mediator from '../utilities/mediator';
import log from '../utilities/log';

import { ApiErrorResponse, Organization } from '../models';
import {
  mutationOptions,
  queryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { EntityState } from 'adc-sirius-module-common';

type OrganizationQueryParams = {
  key?: string;
};

type OrganizationsQueryParams = {
  state?: EntityState;
};

type CreateOrganizationVariables = {
  organization: Organization;
};

type UpdateOrganizationVariables = {
  organizationKey: Organization['key'];
  updatedOrganizationValues: Partial<Organization>;
};

export function organizationsQueryOptions(
  params: OrganizationsQueryParams = {},
  options?: Partial<UseQueryOptions<Organization[]>>,
) {
  return queryOptions<Organization[]>({
    ...options,
    queryKey: ['organizations'],
    queryFn: async () => {
      try {
        return await mediator.request<Organization[]>(
          'organization:getOrganizations',
          { ...params },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function organizationQueryOptions(
  params: OrganizationQueryParams,
  options?: Partial<UseQueryOptions<Organization>>,
) {
  return queryOptions<Organization>({
    ...options,
    queryKey: ['organization', params],
    queryFn: async () => {
      try {
        return await mediator.request<Organization>(
          'organization:getOrganizationByKey',
          {
            ...params,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params?.key,
  });
}

export function createOrganizationMutateOptions(
  options?: Partial<
    UseMutationOptions<Organization, Error, CreateOrganizationVariables>
  >,
) {
  return mutationOptions<Organization, Error, CreateOrganizationVariables>({
    ...options,
    mutationFn: async ({ organization }: CreateOrganizationVariables) => {
      try {
        return await mediator.request<Organization>(
          'organization:createOrganization',
          {
            data: organization,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function updateOrganizationMutateOptions(
  options?: Partial<
    UseMutationOptions<Organization, Error, UpdateOrganizationVariables>
  >,
) {
  return mutationOptions<Organization, Error, UpdateOrganizationVariables>({
    ...options,
    mutationFn: async ({
      updatedOrganizationValues,
    }: UpdateOrganizationVariables) => {
      try {
        return await mediator.request<Organization>(
          'organization:updateOrganization',
          {
            data: updatedOrganizationValues,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}
